import { I18n } from "@assets/strings";
import React from "react";
import styled from "styled-components";
import { Group } from "@domain/group/group";
import { ImageVersions } from "@domain/media/dtos";
import { getBanner } from "@utils/banner";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { AccessModeIndicator } from "@ui/components/group/accessModeIndicator";
import { row, Stack, stack } from "@ui/components/layout";
import { MembersButton } from "@ui/components/profile/community/membersButton";
import { ClosableModal } from "@ui/modals/closableModal";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { GroupMenu } from "./groupMenu";

interface GroupHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
	group: Group;
}
export const GroupHeader: React.FC<GroupHeaderProps> = ({ group, ...htmlProps }) => {
	const { id, banner, name, categories, description, administrators: teachersList, memberCounts, rules } = group;
	const isOpen = group.type === "DISCUSSION_GROUP" ? group.accessMode === "OPEN" : group.visibility === "PUBLIC";
	const { createModal } = useModalTools();

	return (
		<Container {...htmlProps}>
			<GroupInfos>
				<Stack gap={10}>
					<Title>{name}</Title>
					<Description>{description}</Description>
					<MembersCount>
						{I18n.t(`group.${group.type}.thumbnail.members_count`, { count: memberCounts.MEMBER ?? 0 })}
					</MembersCount>
				</Stack>
				<Stack gap={13}>
					<Primary>
						<b>{I18n.t(`group.${group.type}.thumbnail.animated_by`)}</b>
					</Primary>
					<MembersButton sample={teachersList} count={teachersList.length} spacing={9} imageSize={"M"} />
				</Stack>
				{!!rules.getPlainText() && (
					<TertiaryButton
						onClick={() =>
							createModal((close) => (
								<RulesModal close={close} title={I18n.t("group.rules.title")}>
									<DraftRenderer content={rules} />
								</RulesModal>
							))
						}
					>
						{I18n.t("group.rules.button")}
					</TertiaryButton>
				)}
			</GroupInfos>
			<ImageWrapper>
				<StyledBackgroundImage
					image={banner}
					defaultUrl={!banner ? getBanner(id) : undefined}
					size={ImageVersions.MEDIUM}
				/>
				{categories[0] && categories[0] !== "OTHERS" && <Category category={categories[0]} />}
			</ImageWrapper>
			{<StyledAccessModeIndicator closed={!isOpen} />}
			<StyledMenu group={group} />
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	position: relative;
	border: none;
	overflow: hidden;

	@media ${mobile} {
		${stack(0, "flex-start", "stretch", "reverse")};
	}

	@media ${desktop} {
		${row(0, "flex-start", "stretch")};
	}
`;

const GroupInfos = styled.div`
	padding: 60px 55px;
	@media ${mobile} {
		padding: 20px;
	}
	${stack(20)};
	@media ${desktop} {
		width: 50%;
		flex-grow: 0;
		flex-shrink: 0;
	}
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.subtitle}
`;

const Description = styled.div`
	font-weight: 500;
`;

const MembersCount = styled.div`
	${({ theme }) => theme.textStyles.secondary};
	font-weight: 700;
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	width: 100%;
	height: 100%;
	@media ${mobile} {
		height: 220px;
	}
`;

const Category = styled(CategoryLabel)`
	display: inline-block;
	position: absolute;
	top: 22px;
	max-width: 65%;
	right: 60px;
`;

const ImageWrapper = styled.div`
	min-height: 70px;
	@media ${desktop} {
		width: 50%;
		flex-grow: 0;
		flex-shrink: 0;
	}
`;

const StyledMenu = styled(GroupMenu)`
	position: absolute;
	right: 15px;
	top: 22px;
	background: #fff;
	@media ${mobile} {
		right: 12px;
	}
`;

const StyledAccessModeIndicator = styled(AccessModeIndicator)`
	position: absolute;
	top: 12px;
	left: 12px;
`;

const RulesModal = styled(ClosableModal)`
	${gap(20)};
`;
